import { Quote } from '@shared/types/quote';
import { Location } from 'clerk_common';
import { StandardFreightOrderEquipmentType } from 'clerk_common/enums';
import {
  getAddressFromLocation,
  getDeliveryFromFreightOrder,
  getEquipmentTypesFromFreightOrder,
  getPickupFromFreightOrder,
} from 'clerk_common/templates/freight_order/fieldExtractors/fieldExtractors';
import { getValue } from 'clerk_common/templates/getValue';
import { Address } from 'clerk_common/types/address';
import { toLower } from 'lodash';

const isLocalDev = (): boolean => {
  try {
    return window.location.hostname === 'localhost';
  } catch {
    return false;
  }
};

const datOneDomain = () =>
  isLocalDev() ? 'https://one.staging.dat.com' : 'https://one.dat.com';

const DAT_LOAD_SEARCH_URL = 'https://one.dat.com/search-loads-ow';
const DAT_EQUIPMENT_SEARCH_URL_PATH = '/search-equipment';
// NOTE(parlato): Sample URL https://iq.dat.com/rateview/san-francisco-ca~jacksonville-fl~reefer
const DAT_RATE_VIEW_BASE_URL = 'https://iq.dat.com/rateview';

export const VOOMA_QUERY_PARAM_KEY = 'source';
export const VOOMA_QUERY_PARAM_VALUE = 'vooma';

const locationToDATString = (address: any): string => {
  if (typeof address === 'string') return address;

  const { city, state, zip } = address;

  if (zip) return zip;

  if (city && state) return `${city}, ${state}`;

  return '';
};

const sanitizeDATCity = (city: string): string => {
  return city.replace(/ /g, '-').replace(/[^a-zA-Z0-9-]/g, '');
};

export const locationToDATRateViewString = (address: any): string | null => {
  if (typeof address === 'string') return null;

  const { city, state, zip } = address;

  if (city && state) return toLower(`${sanitizeDATCity(city)}-${state}`);

  if (zip) return toLower(zip);

  return null;
};

export const getPickupAddressFromQuote = (
  quote: Quote
): string | Address | undefined => {
  const pickup = quote.extractedData
    ? getPickupFromFreightOrder(quote.extractedData)
    : undefined;
  if (!pickup?.location) return '';

  const location = getValue(pickup.location) as Location;
  return getAddressFromLocation(location);
};

export const getDestinationAddressFromQuote = (
  quote: Quote
): string | Address | undefined => {
  const destination = quote.extractedData
    ? getDeliveryFromFreightOrder(quote.extractedData)
    : undefined;

  if (!destination?.location) return '';

  const location = getValue(destination.location) as Location;
  return getAddressFromLocation(location);
};

const getDATOriginParam = (quote: Quote): string => {
  const address = getPickupAddressFromQuote(quote);
  return locationToDATString(address);
};

const getDATRateViewOriginParam = (quote: Quote): string => {
  const address = getPickupAddressFromQuote(quote);
  return locationToDATRateViewString(address) ?? 'unknown';
};

const getDATDestinationParam = (quote: Quote): string => {
  const address = getDestinationAddressFromQuote(quote);
  return locationToDATString(address);
};

const getDATRateViewDestinationParam = (quote: Quote): string => {
  const address = getDestinationAddressFromQuote(quote);
  return locationToDATRateViewString(address) ?? 'unknown';
};

const equipmentTypeToDATLoadSearchEquipmentType = (
  equipmentType: StandardFreightOrderEquipmentType
): string => {
  switch (equipmentType) {
    case 'CONTAINER':
      return 'Containers';
    case 'DOUBLE_DROP':
      return 'Double Drop';
    case 'DRY_VAN':
      return 'Van';
    case 'FLATBED':
      return 'Flatbeds';
    case 'HOT_SHOT':
      return 'Van Hotshot';
    case 'LOW_BOY':
      return 'Lowboy';
    case 'POWER_ONLY':
      return 'Power Only';
    case 'REEFER':
      return 'Reefers';
    case 'REMOVABLE_GOOSENECK':
      return 'Removable Gooseneck';
    case 'STEP_DECK':
      return 'Step Deck';
    case 'STRAIGHT_TRUCK':
      return 'Straight Box Truck';
    case 'OTHER':
      return 'Other Equipment';
    default:
      return 'Other Equipment';
  }
};

const equipmentTypeToDATRateViewEquipmentType = (
  equipmentType: StandardFreightOrderEquipmentType
): string => {
  switch (equipmentType) {
    case 'DRY_VAN':
      return 'Van';
    case 'FLATBED':
      return 'Flatbed';
    case 'REEFER':
      return 'Reefer';
    default:
      return 'Van';
  }
};

const getDATRateViewEquipmentParam = (quote: Quote): string => {
  const equipmentTypes = getEquipmentTypesFromFreightOrder(quote.extractedData);

  if (!equipmentTypes?.length) return '';

  const equipmentType = equipmentTypes[0];
  return equipmentTypeToDATRateViewEquipmentType(
    equipmentType as StandardFreightOrderEquipmentType
  );
};

const getDATLoadSearchEquipmentParam = (quote: Quote): string => {
  const equipmentTypes = getEquipmentTypesFromFreightOrder(quote.extractedData);

  if (!equipmentTypes?.length) return '';

  const equipmentType = equipmentTypes[0];
  return equipmentTypeToDATLoadSearchEquipmentType(
    equipmentType as StandardFreightOrderEquipmentType
  );
};

const getBaseVoomaParams = (): URLSearchParams => {
  const params = new URLSearchParams();
  params.set(VOOMA_QUERY_PARAM_KEY, VOOMA_QUERY_PARAM_VALUE);
  return params;
};

const getDATLoadSearchQueryParams = (quote: Quote): URLSearchParams => {
  const params = getBaseVoomaParams();

  const origin = getDATOriginParam(quote);
  const destination = getDATDestinationParam(quote);
  const equipment = getDATLoadSearchEquipmentParam(quote);

  origin && params.set('origin-automation', origin);
  destination && params.set('destination-automation', destination);
  equipment && params.set('equip-automation', equipment);
  return params;
};

export const getDATLoadSearchQueryParamString = (quote: Quote): string => {
  return getDATLoadSearchQueryParams(quote).toString();
};

export const getDATRateViewQueryPathname = (quote: Quote): string => {
  const origin = getDATRateViewOriginParam(quote);
  const destination = getDATRateViewDestinationParam(quote);
  const equipment = toLower(getDATRateViewEquipmentParam(quote));
  const params = [origin, destination, equipment];

  return params.join('~');
};

export const getDATLoadSearchUrl = (quote: Quote): string => {
  const queryString = getDATLoadSearchQueryParams(quote);
  return `${DAT_LOAD_SEARCH_URL}?${queryString}`;
};

export const getDATRateViewUrl = (quote: Quote): string => {
  const pathname = getDATRateViewQueryPathname(quote);
  return `${DAT_RATE_VIEW_BASE_URL}/${pathname}`;
};

export type DATEquipmentSearchParams = {
  origin?: { city?: string; state?: string; zip?: string };
  destination?: { city?: string; state?: string; zip?: string };
  equipmentType?: StandardFreightOrderEquipmentType;
  startDate?: string;
  endDate?: string;
};

export const getDATEquipmentSearchUrl = (
  params: DATEquipmentSearchParams
): string => {
  const queryParams = getBaseVoomaParams();

  const origin = params.origin ? locationToDATString(params.origin) : '';
  const destination = params.destination
    ? locationToDATString(params.destination)
    : '';
  const equipment = params.equipmentType
    ? equipmentTypeToDATLoadSearchEquipmentType(params.equipmentType)
    : '';

  origin && queryParams.set('origin-automation', origin);
  destination && queryParams.set('destination-automation', destination);
  equipment && queryParams.set('equip-automation', equipment);

  if (params.startDate || params.endDate) {
    const dateValue = [params.startDate ?? '', params.endDate ?? ''].join(',');
    queryParams.set('daterange-automation', dateValue);
  }

  return `${datOneDomain()}${DAT_EQUIPMENT_SEARCH_URL_PATH}?${queryParams}`;
};

const DATE_PREFIX_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;

const toDATDateString = (isoDateTime: string): string => {
  // NOTE(farzaan): Parse the YYYY-MM-DD prefix directly so we don't shift the
  // calendar day due to the viewer's local timezone (DAT's date picker is
  // date-only, and movement dateTimes may be date-only or include a UTC/offset
  // suffix).
  const match = DATE_PREFIX_PATTERN.exec(isoDateTime);
  if (match) {
    const [, year, month, day] = match;
    return `${Number(month)}/${Number(day)}/${year}`;
  }

  const date = new Date(isoDateTime);
  if (isNaN(date.getTime())) return '';
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
};

export type DATDateExtractionStop = {
  stopType?: string | null;
  dateTimeRanges?: Array<{
    start?: { dateTime?: string | null } | null;
  } | null> | null;
};

export const extractDATDatesFromStops = (
  stops?: DATDateExtractionStop[] | null
): { startDate?: string; endDate?: string } => {
  if (!stops?.length) return {};

  const pickupStop = stops.find((s) => s?.stopType === 'PICKUP');
  const deliveryStop = [...stops]
    .reverse()
    .find((s) => s?.stopType === 'DELIVERY');

  const pickupDateTime = pickupStop?.dateTimeRanges?.[0]?.start?.dateTime;
  const deliveryDateTime = deliveryStop?.dateTimeRanges?.[0]?.start?.dateTime;

  const startDate = pickupDateTime
    ? toDATDateString(pickupDateTime)
    : undefined;
  const endDate = deliveryDateTime
    ? toDATDateString(deliveryDateTime)
    : undefined;

  return {
    ...(startDate ? { startDate } : {}),
    ...(endDate ? { endDate } : {}),
  };
};
